import { describeEvent, EVENT_FORMAT_LABEL, type TournamentEvent } from "../lib/types.js";

/**
 * What an event is, in three levels and no more — the same three wherever an event is shown:
 *
 *   1. its name (the caller's heading or row title),
 *   2. one line of facts in mono — format, who, singles or doubles, how many are in — with an
 *      Unrated tag when it doesn't count,
 *   3. the organiser's description, as ordinary body text.
 *
 * Event rows and pages had grown four or five text styles at nearly the same size and grey
 * (an eyebrow, a facts line, a "doesn't count" line, the description, a hint), so nothing
 * stood out. Status — in, queued, closed, who can enter — is not text here; it is a stamp or
 * a checklist where the screen needs one.
 */
export function EventFacts({
  event,
  showFormat = true,
}: {
  event: TournamentEvent;
  /** Off where the format is already said once for every event, as in an auction tournament. */
  showFormat?: boolean;
}) {
  const facts = [showFormat ? EVENT_FORMAT_LABEL[event.format] : null, describeEvent(event)]
    .filter(Boolean)
    .join(" · ");

  return (
    <span className="font-mono flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-ink-muted">
      <span>{facts}</span>
      {/* A tag, not a sentence: the first thing a parent asks, said in a word. */}
      {event.countsForRating ? null : <UnratedTag />}
    </span>
  );
}

export function UnratedTag() {
  return (
    <span
      className="border px-1 text-[9px] font-semibold uppercase tracking-[0.12em]"
      style={{ borderColor: "var(--color-ink-muted)" }}
      title="Doesn't count towards ratings"
    >
      Unrated
    </span>
  );
}

/** Facts, then the description: the top of the event page and the register screen. */
export function EventSummary({
  event,
  showFormat = true,
}: {
  event: TournamentEvent;
  showFormat?: boolean;
}) {
  return (
    <div className="flex flex-col gap-3">
      <EventFacts event={event} showFormat={showFormat} />
      {/* A text node, so markup shows as typed; pre-line keeps the organiser's paragraphs. */}
      {event.description ? (
        <p className="whitespace-pre-line text-[15px] leading-relaxed">{event.description}</p>
      ) : null}
    </div>
  );
}
